import React, { useEffect, useState } from 'react';
import api, { formatApiError, resolveAssetUrl } from '../../lib/api';
import { ImageField } from './ContentEditor';

const EMPTY = { slug: '', name: '', title: '', group: 'investment', summary: '', tenure: '', email: '', phone: '', photo_url: '', detail: [], order: 0 };

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const TeamManager = () => {
  const [members, setMembers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(null); // member being edited, or 'new'
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const refresh = async () => {
    const { data } = await api.get('/team');
    setMembers(data || []);
    setLoaded(true);
  };
  useEffect(() => { refresh(); }, []);

  const startNew = () => { setDraft({ ...EMPTY, order: members.length }); setEditing('new'); setErr(''); };
  const startEdit = (m) => { setDraft({ ...EMPTY, ...m, detail: m.detail || [] }); setEditing(m.id); setErr(''); };
  const cancel = () => { setEditing(null); setDraft(EMPTY); setErr(''); };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const payload = {
        ...draft,
        slug: draft.slug || slugify(draft.name),
        detail: (draft.detail || []).filter((d) => d && d.trim()),
      };
      if (editing === 'new') {
        await api.post('/team', payload);
      } else {
        await api.put(`/team/${editing}`, payload);
      }
      await refresh();
      cancel();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    await api.delete(`/team/${m.id}`);
    await refresh();
  };

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= members.length) return;
    const next = [...members];
    [next[idx], next[target]] = [next[target], next[idx]];
    setMembers(next);
    await api.post('/team/reorder', { ids: next.map((m) => m.id) });
  };

  const setDetailLine = (i, v) => {
    const d = [...(draft.detail || [])];
    d[i] = v;
    setDraft({ ...draft, detail: d });
  };
  const addDetailLine = () => setDraft({ ...draft, detail: [...(draft.detail || []), ''] });
  const removeDetailLine = (i) => setDraft({ ...draft, detail: draft.detail.filter((_, k) => k !== i) });

  return (
    <div data-testid="team-manager">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-sans text-[11px] tracking-[0.3em] text-navy/60">MANAGING</p>
          <h1 className="font-serif text-4xl text-navy mt-1">Team members</h1>
          <p className="font-sans text-sm text-navy/60 mt-2">Floating-head grid on the Team page is sorted by order. Reorder with ↑ ↓.</p>
        </div>
        {!editing && (
          <button onClick={startNew} data-testid="team-add" className="font-sans text-[11px] tracking-[0.3em] bg-navy text-cream px-6 py-3 hover:bg-navydark">+ ADD MEMBER</button>
        )}
      </div>

      {editing && (
        <div className="mt-10 border border-navy/20 bg-cream2/40 p-8" data-testid="team-editor">
          <h2 className="font-serif text-2xl text-navy">{editing === 'new' ? 'New member' : 'Edit member'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <Field label="Name" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v, slug: draft.slug || slugify(v) })} testid="te-name" />
            <Field label="Slug" value={draft.slug} onChange={(v) => setDraft({ ...draft, slug: v })} testid="te-slug" />
            <Field label="Title" value={draft.title} onChange={(v) => setDraft({ ...draft, title: v })} testid="te-title" />
            <div>
              <label className="font-sans text-[11px] tracking-[0.3em] text-navy/60 block mb-2">GROUP</label>
              <select value={draft.group} onChange={(e) => setDraft({ ...draft, group: e.target.value })}
                data-testid="te-group"
                className="w-full px-3 py-2 border border-navy/20 bg-cream2 font-sans text-navy">
                <option value="founder">Founder</option>
                <option value="investment">Investment</option>
                <option value="advisory">Advisory</option>
              </select>
            </div>
            <Field label="Tenure (optional)" value={draft.tenure} onChange={(v) => setDraft({ ...draft, tenure: v })} testid="te-tenure" />
            <Field label="Email (optional)" value={draft.email} onChange={(v) => setDraft({ ...draft, email: v })} testid="te-email" />
            <Field label="Phone (optional)" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} testid="te-phone" />
          </div>
          <div className="mt-6">
            <label className="font-sans text-[11px] tracking-[0.3em] text-navy/60 block mb-2">SUMMARY (SHORT BIO)</label>
            <textarea value={draft.summary || ''} onChange={(e) => setDraft({ ...draft, summary: e.target.value })} rows={3}
              data-testid="te-summary"
              className="w-full px-3 py-2 border border-navy/20 bg-cream2 font-serif text-navy" />
          </div>
          <div className="mt-6">
            <label className="font-sans text-[11px] tracking-[0.3em] text-navy/60 block mb-2">EXPERIENCE / BIO BULLETS</label>
            <div className="space-y-2">
              {(draft.detail || []).map((line, i) => (
                <div key={i} className="flex gap-2">
                  <input type="text" value={line} onChange={(e) => setDetailLine(i, e.target.value)}
                    className="flex-1 px-3 py-2 border border-navy/20 bg-cream2 font-serif text-navy text-sm" />
                  <button onClick={() => removeDetailLine(i)} className="font-sans text-[10px] tracking-[0.3em] text-red-700 px-3">×</button>
                </div>
              ))}
              <button onClick={addDetailLine} data-testid="te-add-detail"
                className="font-sans text-[10px] tracking-[0.3em] text-navy/60 hover:text-navy border border-dashed border-navy/30 px-4 py-2">+ ADD LINE</button>
            </div>
          </div>
          <div className="mt-6">
            <label className="font-sans text-[11px] tracking-[0.3em] text-navy/60 block mb-2">HEADSHOT</label>
            <ImageField value={draft.photo_url || ''} onChange={(v) => setDraft({ ...draft, photo_url: v })} testid="te-photo" />
          </div>
          {err && <p className="font-sans text-sm text-red-700 mt-4" data-testid="te-error">{err}</p>}
          <div className="mt-8 flex gap-3">
            <button onClick={save} disabled={saving || !draft.name || !draft.title} data-testid="te-save"
              className="font-sans text-[11px] tracking-[0.3em] bg-navy text-cream px-6 py-3 hover:bg-navydark disabled:opacity-60">
              {saving ? 'SAVING…' : 'SAVE'}
            </button>
            <button onClick={cancel} data-testid="te-cancel"
              className="font-sans text-[11px] tracking-[0.3em] text-navy/70 px-6 py-3 border border-navy/30 hover:bg-navy/5">CANCEL</button>
          </div>
        </div>
      )}

      {!editing && (
        <div className="mt-10">
          {!loaded ? (
            <p className="font-sans text-sm text-navy/60">Loading…</p>
          ) : members.length === 0 ? (
            <p className="font-sans text-sm text-navy/60">No team members yet. Click + ADD MEMBER to create one.</p>
          ) : (
            <div className="divide-y divide-navy/10 border-t border-b border-navy/15">
              {members.map((m, idx) => (
                <div key={m.id} className="flex items-center gap-5 py-4" data-testid={`team-row-${m.slug}`}>
                  <div className="w-14 h-14 rounded-full overflow-hidden bg-cream2 flex-shrink-0">
                    {m.photo_url && <img src={resolveAssetUrl(m.photo_url)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-xl text-navy leading-tight">{m.name}</p>
                    <p className="font-serif italic text-sm text-navy/60 mt-0.5">{m.title}</p>
                  </div>
                  <span className="font-sans text-[10px] tracking-[0.3em] text-navy/50 uppercase">{m.group}</span>
                  <div className="flex items-center gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0} className="font-sans text-sm px-2 py-1 text-navy/60 hover:text-navy disabled:opacity-30" aria-label="Move up">↑</button>
                    <button onClick={() => move(idx, 1)} disabled={idx === members.length - 1} className="font-sans text-sm px-2 py-1 text-navy/60 hover:text-navy disabled:opacity-30" aria-label="Move down">↓</button>
                  </div>
                  <button onClick={() => startEdit(m)} data-testid={`team-edit-${m.slug}`} className="font-sans text-[10px] tracking-[0.3em] text-navy/70 hover:text-navy">EDIT</button>
                  <button onClick={() => remove(m)} data-testid={`team-remove-${m.slug}`} className="font-sans text-[10px] tracking-[0.3em] text-red-700 hover:underline">REMOVE</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="font-sans text-[11px] tracking-[0.3em] text-navy/60 block mb-2">{label.toUpperCase()}</label>
    <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2 border border-navy/20 bg-cream2 font-serif text-navy focus:outline-none focus:border-navy" />
  </div>
);

export default TeamManager;
